import React, { useState, useEffect } from "react";
import LifeSaverLayout from "../../components/LifeSaverLayout";
import Modal from "../../components/Modal";
import api, { baseURL } from "../../services/api";
import { toast } from "react-toastify";
import {
  FiSend,
  FiPlus,
  FiMessageCircle,
  FiClock,
  FiCheckCircle,
  FiMail,
  FiSearch,
  FiPaperclip,
  FiFile,
  FiImage,
} from "react-icons/fi";

const CommunicateToAdmin = () => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    subject: "",
    message: "",
  });
  const [voiceFile, setVoiceFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);

  // Search filter
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCommunications();
  }, []);

  const fetchCommunications = async () => {
    try {
      const response = await api.get("/member-communications/my");
      setCommunications(response.data);
    } catch (error) {
      console.error("Error fetching communications:", error);
      toast.error("Failed to load communications");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }

    setSubmitting(true);
    try {
      const data = new FormData();
      data.append("subject", formData.subject);
      data.append("message", formData.message);
      if (voiceFile) {
        data.append("voice_file", voiceFile);
      }
      if (documentFile) {
        data.append("document_file", documentFile);
      }

      await api.post("/member-communications", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Message sent to admin successfully!");
      setShowModal(false);
      setFormData({ subject: "", message: "" });
      setVoiceFile(null);
      setDocumentFile(null);
      fetchCommunications();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.message || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewCommunication = (comm) => {
    setSelectedComm(comm);
    setShowViewModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "unread":
        return <span className="badge badge-warning">Pending</span>;
      case "read":
        return <span className="badge badge-info">Read</span>;
      case "replied":
        return <span className="badge badge-success">Replied</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const filteredCommunications = communications.filter(
    (comm) =>
      comm.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comm.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <LifeSaverLayout>
        <div className="spinner"></div>
      </LifeSaverLayout>
    );
  }

  return (
    <LifeSaverLayout>
      <div className="page-header">
        <h1 className="page-title">
          {/* <FiSend style={{ marginRight: "0.5rem" }} /> */}
          Communicate to Admin
        </h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <FiPlus style={{ marginRight: "0.5rem" }} />
          New Message
        </button>
      </div>

      {/* Search */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem",
          }}
        >
          <FiSearch style={{ color: "#666" }} />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ border: "none", outline: "none", flex: 1 }}
          />
        </div>
      </div>

      {/* Communications List */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            {/* <FiMessageCircle style={{ marginRight: "0.5rem" }} /> */}
            My Messages ({filteredCommunications.length})
          </h2>
        </div>

        {filteredCommunications.length > 0 ? (
          <div className="communications-list">
            {filteredCommunications.map((comm) => (
              <div
                key={comm.id}
                className="communication-item"
                onClick={() => handleViewCommunication(comm)}
                style={{
                  padding: "1rem",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f9f9f9")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "0.5rem",
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>
                    {comm.subject}
                  </h3>
                  {getStatusBadge(comm.status)}
                </div>
                <p
                  style={{
                    margin: "0.5rem 0",
                    color: "#666",
                    fontSize: "0.9rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {comm.message}
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                    fontSize: "0.8rem",
                    color: "#888",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <FiClock size={14} />
                    {formatDate(comm.created_at)}
                  </span>
                  {comm.status === "replied" && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        color: "#28a745",
                      }}
                    >
                      <FiCheckCircle size={14} />
                      Admin replied
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              color: "#666",
            }}
          >
            {/* <FiMail size={48} style={{ marginBottom: "1rem", opacity: 0.5 }} /> */}
            <p>No messages yet. Click "New Message" to contact admin.</p>
          </div>
        )}
      </div>

      {/* New Message Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setFormData({ subject: "", message: "" });
          setVoiceFile(null);
          setDocumentFile(null);
        }}
        title="Send Message to Admin"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Subject *</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
              placeholder="Enter message subject"
              required
            />
          </div>

          <div className="form-group">
            <label>Message *</label>
            <textarea
              value={formData.message}
              onChange={(e) =>
                setFormData({ ...formData, message: e.target.value })
              }
              placeholder="Type your message here..."
              rows={5}
              required
              style={{ resize: "vertical" }}
            />
          </div>

          <div className="form-group">
            <label>Voice Message (Optional)</label>
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => setVoiceFile(e.target.files[0])}
            />
            {voiceFile && (
              <small style={{ color: "#666" }}>
                Selected: {voiceFile.name}
              </small>
            )}
          </div>

          <div className="form-group">
            <label>
              {/* <FiPaperclip style={{ marginRight: "0.25rem" }} /> */}
              Document/Image (Optional)
            </label>
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setDocumentFile(e.target.files[0])}
            />
            <small
              style={{ color: "#888", display: "block", marginTop: "0.25rem" }}
            >
              Supported: Images (JPEG, PNG, GIF, WebP) and PDF files (max 10MB)
            </small>
            {documentFile && (
              <small style={{ color: "#666", display: "block" }}>
                Selected: {documentFile.name}
              </small>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "flex-end",
              marginTop: "1.5rem",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowModal(false);
                setFormData({ subject: "", message: "" });
                setVoiceFile(null);
                setDocumentFile(null);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Message Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedComm(null);
        }}
        title="Message Details"
      >
        {selectedComm && (
          <div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>Subject</label>
              <p style={{ margin: "0.25rem 0" }}>{selectedComm.subject}</p>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>Status</label>
              <div style={{ marginTop: "0.25rem" }}>
                {getStatusBadge(selectedComm.status)}
              </div>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>
                Your Message
              </label>
              <p
                style={{
                  margin: "0.25rem 0",
                  whiteSpace: "pre-wrap",
                  backgroundColor: "#f5f5f5",
                  padding: "1rem",
                  borderRadius: "8px",
                }}
              >
                {selectedComm.message}
              </p>
            </div>

            {selectedComm.voice_file_path && (
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontWeight: 600, color: "#666" }}>
                  Voice Message
                </label>
                <audio controls style={{ width: "100%", marginTop: "0.5rem" }}>
                  <source src={`${baseURL}${selectedComm.voice_file_path}`} />
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}

            {selectedComm.document_file_path && (
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    fontWeight: 600,
                    color: "#666",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FiPaperclip />
                  Attached Document
                </label>
                <div style={{ marginTop: "0.5rem" }}>
                  {selectedComm.document_file_path.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i
                  ) ? (
                    <div>
                      <img
                        src={`${baseURL}${selectedComm.document_file_path}`}
                        alt="Attached document"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                        }}
                      />
                      <div style={{ marginTop: "0.5rem" }}>
                        <a
                          href={`${baseURL}${selectedComm.document_file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary"
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.8rem",
                          }}
                        >
                          <FiImage style={{ marginRight: "0.25rem" }} />
                          View Full Size
                        </a>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={`${baseURL}${selectedComm.document_file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <FiFile />
                      View/Download PDF
                    </a>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>Sent On</label>
              <p style={{ margin: "0.25rem 0" }}>
                {formatDate(selectedComm.created_at)}
              </p>
            </div>

            {selectedComm.status === "replied" && selectedComm.admin_reply && (
              <div
                style={{
                  marginTop: "1.5rem",
                  padding: "1rem",
                  backgroundColor: "#e8f5e9",
                  borderRadius: "8px",
                  borderLeft: "4px solid #28a745",
                }}
              >
                <label
                  style={{
                    fontWeight: 600,
                    color: "#28a745",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FiCheckCircle />
                  Admin Reply
                </label>
                <p style={{ margin: "0.5rem 0", whiteSpace: "pre-wrap" }}>
                  {selectedComm.admin_reply}
                </p>
                <small style={{ color: "#666" }}>
                  Replied on: {formatDate(selectedComm.replied_at)}
                </small>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedComm(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </LifeSaverLayout>
  );
};

export default CommunicateToAdmin;
